import React, { useEffect, useRef, useState, useCallback } from "react";
import { getTicketPDF } from "../../fetchers/tickets";
import { useParams as useReactRouterParams } from "react-router-dom";
import { GlobalWorkerOptions, getDocument, PDFDocumentProxy } from "pdfjs-dist";
import logo from "../../assets/logos/eventim_mc_pos.png";

GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

function useParams(): { eventId: number; token: string } {
  const params = useReactRouterParams();
  return {
    eventId: params.eventId ? parseInt(params.eventId, 10) : 0,
    token: params.ticketToken || "",
  };
}

const TicketViewer: React.FC = () => {
  const { eventId, token } = useParams();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfRef = useRef<PDFDocumentProxy | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isMobile = window.innerWidth <= 768;

  const renderPage = useCallback(
    async (pageNumber: number) => {
      const pdf = pdfRef.current;
      if (!pdf || !canvasRef.current) return;

      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      if (!context) return;

      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 0.8 });
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext = {
        canvasContext: context,
        viewport,
      };

      await page.render(renderContext).promise;
    },
    []
  );

  // Fetch and load PDF
  useEffect(() => {
    const fetchPDF = async () => {
      setLoading(true);
      setError(null);
      try {
        const pdfBuffer = await getTicketPDF({ eventId, token });
        if (!pdfBuffer) {
          setError("לא ניתן לשלוף את הכרטיס.");
          return;
        }

        const arrayBuffer = pdfBuffer instanceof ArrayBuffer ? pdfBuffer : new Uint8Array(pdfBuffer).buffer;
        const loadedPdf = await getDocument({ data: arrayBuffer }).promise;

        pdfRef.current = loadedPdf;
        setNumPages(loadedPdf.numPages);
        setPageNum(1); // Reset to first page
        requestAnimationFrame(() => renderPage(1)); // Render first page ASAP
      } catch (err) {
        console.error("שגיאה בטעינת ה-PDF:", err);
        setError("שגיאה בטעינת הכרטיס.");
      } finally {
        setLoading(false);
      }
    };

    fetchPDF();
  }, [eventId, token, renderPage]);

  // Render when pageNum changes
  useEffect(() => {
    if (pdfRef.current) {
      requestAnimationFrame(() => renderPage(pageNum));
    }
  }, [pageNum, renderPage]);

  const goToPrevPage = () => setPageNum((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNum((prev) => Math.min(prev + 1, numPages));

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        flexDirection: "column",
      }}
    >
      <img
        src={logo}
        alt="Logo"
        style={{ marginBottom: "-20px", maxWidth: isMobile ? "50%" : "250px" }}
      />

      {loading && (
        <div style={{ marginTop: "2rem", textAlign: "center" }}>
          <div
            style={{
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #007bff",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 1rem auto",
            }}
          />
          <p style={{ fontWeight: "bold", direction: "rtl", }}>טוען כרטיסים...</p>
        </div>
      )}

      {error && <p style={{ color: "red", marginTop: "1rem", direction: "rtl", }}>{error}</p>}

      {!loading && !error && (
        <>
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: "100%",
              borderRadius: "8px",
              boxShadow: "0 0 10px rgba(0,0,0,0.2)",
              marginTop: "1rem",
            }}
          />
          {numPages > 1 && (
            <div
              style={{
                marginTop: "1rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "1rem",
                direction: "rtl",
              }}
            >
              <button
                onClick={goToPrevPage}
                disabled={pageNum <= 1}
                style={{
                  padding: "0.5rem 1rem",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: pageNum <= 1 ? "not-allowed" : "pointer",
                  opacity: pageNum <= 1 ? 0.5 : 1,
                  fontWeight: "bold",
                  fontSize: "1rem",
                }}
              >
               → הקודם 
              </button>
              <span style={{ fontWeight: "bold", fontSize: "1rem" }}>
                עמוד {pageNum} מתוך {numPages}
              </span>
              <button
                onClick={goToNextPage}
                disabled={pageNum >= numPages}
                style={{
                  padding: "0.5rem 1rem",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: pageNum >= numPages ? "not-allowed" : "pointer",
                  opacity: pageNum >= numPages ? 0.5 : 1,
                  fontWeight: "bold",
                  fontSize: "1rem",
                }}
              >
                 הבא ←
              </button>

            </div>
          )}
        </>
      )}

      {/* CSS for Spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default TicketViewer;
